<template>
  <footer
    class="site-footer"
    :class="{ 'home-snap-section': isHomePage && !compact, 'site-footer--compact': compact }"
  >
    <div ref="footerRef" class="footer">
      <div class="footer__inner">
        <header v-if="!compact" class="footer__header footer__header--desktop reveal">
          <h2 class="footer__heading">UNIVERSO // {{ storeName }}</h2>
          <p v-if="storeSlogan" class="footer__tagline">{{ storeSlogan }}</p>
        </header>

        <div class="footer__grid">
          <div class="footer__col footer__col--shop reveal-left">
            <h3 class="footer__col-title">{{ t('footer.colShop') }}</h3>
            <ul class="footer__links">
              <li><a href="/productos">{{ t('footer.products') }}</a></li>
              <li><a href="/carrito/">{{ t('footer.cart') }}</a></li>
              <li v-for="cat in footerCategories" :key="cat.id">
                <a :href="'/productos?categories=' + encodeURIComponent(cat.name)">{{ cat.label }}</a>
              </li>
            </ul>
          </div>

          <div class="footer__col footer__col--nav reveal">
            <h3 class="footer__col-title">{{ t('footer.colNav') }}</h3>
            <ul class="footer__links">
              <li v-for="link in navigationFooterLinks" :key="link.id">
                <a :href="link.href">
                  <Pickable
                    v-if="isPickMode && isConfiguredFooterLink(link)"
                    tag="span"
                    :label="'Footer · ' + (link.href || 'enlace')"
                    @pick="pickFooterLink(link)"
                  >
                    {{ link.label }}
                  </Pickable>
                  <template v-else>{{ link.label }}</template>
                </a>
              </li>
            </ul>
          </div>

          <div class="footer__col footer__col--nav-secondary reveal">
            <h3 class="footer__col-title">{{ t('footer.colLegal') }}</h3>
            <ul class="footer__links">
              <li v-for="link in legalFooterLinks" :key="link.id">
                <a :href="link.href">
                  <Pickable
                    v-if="isPickMode && isConfiguredFooterLink(link)"
                    tag="span"
                    :label="'Footer · ' + (link.href || 'enlace')"
                    @pick="pickFooterLink(link)"
                  >
                    {{ link.label }}
                  </Pickable>
                  <template v-else>{{ link.label }}</template>
                </a>
              </li>
            </ul>
          </div>

          <div class="footer__col footer__col--contact reveal-right">
            <h3 class="footer__col-title">{{ t('footer.colContact') }}</h3>
            <ul class="footer__links">
              <li v-if="contactEmail">
                <a :href="contactMailto">{{ contactEmail }}</a>
              </li>
              <li v-if="contactPhoneDisplay">
                <a :href="telHref">{{ contactPhoneDisplay }}</a>
              </li>
              <li v-if="whatsappHref && whatsappHref !== '#'">
                <a :href="whatsappHref" target="_blank" rel="noopener noreferrer">WhatsApp</a>
              </li>
            </ul>

            <div v-if="hasSocialLinks" class="footer__social">
              <a
                v-if="instagramUrl"
                :href="instagramUrl"
                target="_blank"
                rel="noopener noreferrer"
              >
                INSTAGRAM
              </a>
              <a
                v-if="socialTiktokUrl"
                :href="socialTiktokUrl"
                target="_blank"
                rel="noopener noreferrer"
              >
                X (TWITTER)
              </a>
              <a
                v-if="socialYoutubeUrl"
                :href="socialYoutubeUrl"
                target="_blank"
                rel="noopener noreferrer"
              >
                YOUTUBE
              </a>
              <a
                v-if="socialFacebookUrl"
                :href="socialFacebookUrl"
                target="_blank"
                rel="noopener noreferrer"
              >
                FACEBOOK
              </a>
            </div>
          </div>
        </div>

        <div v-if="instagramUrl" class="footer__mobile-social reveal">
          <a
            :href="instagramUrl"
            target="_blank"
            rel="noopener noreferrer"
          >
            INSTAGRAM
          </a>
        </div>

        <div class="footer__meta footer__meta--desktop reveal mt-5 pt-5">
          <div class="footer__language" :aria-label="t('language.select')">
            <button
              type="button"
              class="footer__lang-btn"
              :class="{ 'is-active': locale === 'es' }"
              :aria-pressed="locale === 'es'"
              @click="setLocale('es')"
            >
              ESP
            </button>
            <span class="footer__lang-sep">/</span>
            <button
              type="button"
              class="footer__lang-btn"
              :class="{ 'is-active': locale === 'en' }"
              :aria-pressed="locale === 'en'"
              @click="setLocale('en')"
            >
              ENG
            </button>
          </div>
          <span class="footer__copy">&copy; {{ currentYear }} {{ storeName }}</span>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup>
import { onMounted, ref, computed } from 'vue';
import { useRoute } from 'vue-router';
import { useScrollReveal } from '@/composables/useScrollReveal';
import { getCategories } from '@/services/categories';
import { useSiteContact } from '@/composables/useSiteContact';
import { useI18n } from '@/composables/useI18n';
import { localizedCategoryName } from '@/utils/translations';
import { isPickMode, pickElement } from '@/composables/usePickMode';
import Pickable from '@/modules/admin/ui/PickableItem.vue';

defineProps({
  compact: {
    type: Boolean,
    default: false,
  },
});

const route = useRoute();
const currentYear = new Date().getFullYear();

const {
  contactEmail,
  contactPhoneDisplay,
  contactMailto,
  telHref,
  instagramUrl,
  socialTiktokUrl,
  socialYoutubeUrl,
  socialFacebookUrl,
  whatsappHref,
  storeName,
  storeSlogan,
  footerLinks,
} = useSiteContact();

const { locale, t, setLocale } = useI18n();

const DEFAULT_FOOTER_LINKS = [
  { id: 'footer-nav-inicio', label: 'Inicio', href: '/', column: 'navegacion', sort_order: 1 },
  { id: 'footer-nav-contacto', label: 'Contacto', href: '/contacto', column: 'navegacion', sort_order: 2 },
  { id: 'footer-nav-faq', label: 'FAQ', href: '/contacto#faq', column: 'navegacion', sort_order: 3 },
  { id: 'footer-legal-terminos', label: 'Términos y condiciones', href: '/terminos-y-condiciones', column: 'legal', sort_order: 1 },
  { id: 'footer-legal-privacidad', label: 'Política de privacidad', href: '/politica-de-privacidad', column: 'legal', sort_order: 2 },
  { id: 'footer-legal-reembolsos', label: 'Política de reembolsos', href: '/politica-de-reembolsos', column: 'legal', sort_order: 3 },
  { id: 'footer-legal-envios', label: 'Política de envíos', href: '/envios', column: 'legal', sort_order: 4 },
];

const resolvedFooterLinks = computed(() => {
  const configured = Array.isArray(footerLinks.value) ? footerLinks.value : [];
  if (configured.length) {
    return [...configured].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  }
  return DEFAULT_FOOTER_LINKS;
});

const navigationFooterLinks = computed(() => (
  resolvedFooterLinks.value.filter((link) => link.column === 'navegacion')
));

const legalFooterLinks = computed(() => (
  resolvedFooterLinks.value.filter((link) => link.column === 'legal')
));

// ── Modo selección (elegir elemento de la web para editar en el panel) ──
function isConfiguredFooterLink(link) {
  if (!link || !link.id) return false
  const configured = Array.isArray(footerLinks.value) ? footerLinks.value : []
  return configured.some((l) => String(l.id) === String(link.id))
}

function pickFooterLink(link) {
  pickElement({ tab: 'footer', field: `tr-footer-${link.id}` })
}

const footerRef = ref(null);
const categories = ref([]);
const { observe, observeAll } = useScrollReveal();

const isHomePage = computed(() => route.path === '/');

const footerCategories = computed(() => {
  const parents = categories.value.filter((cat) => !cat.parent);
  return parents.slice(0, 3).map((cat) => ({
    ...cat,
    // name nativo para el href (filtro por nombre ES), label visible traducido
    label: localizedCategoryName(cat, locale.value),
  }));
});

const hasSocialLinks = computed(() => {
  return Boolean(
    instagramUrl.value ||
    socialTiktokUrl.value ||
    socialYoutubeUrl.value ||
    socialFacebookUrl.value
  );
});

onMounted(async () => {
  if (footerRef.value) {
    observe(footerRef.value);
    observeAll('.reveal', footerRef.value);
    observeAll('.reveal-left', footerRef.value);
    observeAll('.reveal-right', footerRef.value);
    observeAll('.reveal-scale', footerRef.value);
  }

  try {
    const response = await getCategories();
    const data = response.results || response;
    categories.value = Array.isArray(data) ? data : [];
  } catch (err) {
    console.error('Error cargando categorías en footer:', err);
  }
});
</script>

<style scoped>
.site-footer {
  background-color: var(--store-surface-dark, #0d0d0d);
  color: var(--white-color);
  padding-bottom: env(safe-area-inset-bottom, 0px);
}

.site-footer:not(.site-footer--compact) {
  position: relative;
  z-index: 1;
}

.site-footer:not(.home-snap-section) .footer {
  height: auto;
  min-height: 0;
  padding-top: clamp(1.25rem, 2.5vw, 1.75rem);
  padding-bottom: clamp(1rem, 2vw, 1.5rem);
}

.site-footer:not(.home-snap-section) .footer__inner {
  justify-content: flex-start;
  gap: clamp(0.75rem, 1.5vh, 1.25rem);
}

.site-footer.home-snap-section {
  height: 100vh;
  min-height: 100vh;
  max-height: 100vh;
  overflow: hidden;
  box-sizing: border-box;
}

/* Compact variant: embedded within another section (e.g. FAQ + Footer on home) */
.site-footer--compact {
  height: auto;
  min-height: 0;
  background-color: transparent;
}

.site-footer--compact .footer {
  height: auto;
  min-height: 0;
  padding-top: 0;
  padding-bottom: 0;
}

.site-footer.site-footer--compact:not(.home-snap-section) .footer {
  padding-top: 0;
  padding-bottom: 0;
}

.site-footer--compact .footer__inner {
  gap: clamp(1rem, 2vh, 1.5rem);
  padding-inline: 0;
}

/* En la variante compact el footer no tiene padding propio: el crédito
   se separa del borde inferior con su propio aire. */
.site-footer--compact .footer__meta {
  padding-bottom: 0.9rem;
}

@media (max-width: 991.98px) {
  .site-footer--compact .footer__inner {
    gap: 0.45rem;
    padding-inline: 0;
  }
}

.footer {
  height: 100%;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  padding-top: var(--navbar-total-height, 72px);
  padding-bottom: clamp(1rem, 2vh, 1.5rem);
  box-sizing: border-box;
}

.footer__inner {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: clamp(1.25rem, 2.5vh, 2rem);
  width: 100%;
  margin-inline: auto;
  padding-inline: clamp(1.25rem, 3vw, 2.5rem);
}

.footer__header {
  flex-shrink: 0;
}

.footer__heading {
  font-family: 'Host Grotesk', sans-serif;
  color: var(--footer-heading);
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  margin: 0;
}

.footer__tagline {
  margin: 0.3rem 0 0;
  font-size: 0.75rem;
  line-height: 1.5;
  color: var(--white-color);
  max-width: 28rem;
}

.footer__grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: clamp(1rem, 2vw, 1.75rem);       
}

.footer__col-title {
  font-family: 'Host Grotesk', sans-serif;
  color: var(--footer-heading);
  font-size: 0.65rem !important;
  font-weight: 500;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  margin: 0 0 0.55rem;
}

.footer__links {
  list-style: none;
  padding: 0;
  margin: 0;
}

.footer__links li {
  margin-bottom: 0.3rem;
}

.footer__links a {
  color: #c4c4c4;
  text-decoration: none;
  font-size: 0.55rem !important;
  text-transform: uppercase;
  line-height: 1.35;
  letter-spacing: 0.08em;
  transition: color 0.2s ease, opacity 0.2s ease;
}

.footer__links a:hover {
  color: rgba(255, 255, 255, 0.75);
}

.footer__social {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1rem;
}

.footer__social a {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2rem;
  padding: 0.25rem 0.45rem;
  border: 1px solid rgba(255, 255, 255, 0.35);
  color: var(--white-color);
  font-size: 0.48rem !important;
  font-weight: 500 !important;
  letter-spacing: 0.08em !important;
  text-decoration: none;
  transition: border-color 0.2s ease, color 0.2s ease, background-color 0.2s ease;
}

.footer__social a:hover {
  color: rgba(255, 255, 255, 0.75);
  border-color: rgba(255, 255, 255, 0.55);
  background: rgba(255, 255, 255, 0.06);
}

.footer__meta {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4rem;
  margin-top: auto;
  text-align: center;
  color: var(--white-color);
}

.footer__copy {
  font-size: 0.68rem;
  letter-spacing: 0.06em;
}

.footer__language {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.55rem;
  letter-spacing: 0.08em;
}

.footer__lang-btn {
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--white-color);
  font-family: inherit;
  font-size: inherit;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  cursor: pointer;
  opacity: 0.55;
  transition: opacity 0.2s ease, color 0.2s ease;
}

.footer__lang-btn:hover {
  opacity: 0.85;
}

.footer__lang-btn.is-active {
  opacity: 1;
  color: var(--white-color);
}

.footer__lang-sep {
  opacity: 0.4;
}

.footer__credit {
  display: inline-flex;
  align-items: baseline;
  gap: 0.4em;
  font-size: 0.70rem;
  line-height: 1.4;
  opacity: 0.55;
  transition: opacity 0.2s ease;
}

.footer__credit-link {
  color: var(--white-color);
  font-size: 0.70rem;
  font-weight: 400;
  text-decoration: none;
  border-bottom: 1px solid rgba(255, 255, 255, 0.22);
  transition: border-color 0.2s ease, opacity 0.2s ease;
}

.footer__credit:hover {
  opacity: 0.8;
}

.footer__credit-link:hover {
  border-bottom-color: rgba(255, 255, 255, 0.55);
  opacity: 1;
}

.footer__meta--mobile,
.footer__mobile-social {
  display: none;
}

.footer__mobile-social a {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.35rem 0.55rem;
  border: 1px solid rgba(255, 255, 255, 0.35);
  color: var(--white-color);
  font-size: 0.62rem;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-decoration: none;
  transition: border-color 0.2s ease, color 0.2s ease, background-color 0.2s ease;
}

.footer__mobile-social a:hover {
  color: rgba(255, 255, 255, 0.75);
  border-color: rgba(255, 255, 255, 0.55);
  background: rgba(255, 255, 255, 0.06);
}



.footer__brand {
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  width: 100%;
  margin-top: auto;
}

.footer__logo-link {
  display: block;
  width: clamp(140px, 18vw, 200px);
  opacity: 0.9;
  transition: opacity 0.2s ease;
}

.footer__logo-link:hover {
  opacity: 1;
}

.footer__logo {
  width: 100%;
  height: auto;
  display: block;
  object-fit: contain;
}

@media (max-width: 991.98px) {
  .site-footer.home-snap-section {
    height: auto;
    min-height: auto;
    max-height: none;
    overflow: visible;
  }

  .site-footer:not(.home-snap-section):not(.site-footer--compact) .footer {
    padding-top: 1rem;
    padding-bottom: 0.65rem;
  }

  .site-footer.site-footer--compact:not(.home-snap-section) .footer {
    padding-top: 0;
    padding-bottom: 0;
  }

  .footer {
    height: auto;
    min-height: auto;
    padding-top: 0;
    padding-bottom: 0.65rem;
  }

  .footer__inner {
    flex: none;
    min-height: auto;
    justify-content: flex-start;
    gap: 0.45rem;
    padding-block: 0.65rem;
    padding-inline: 1.25rem;
  }

  .site-footer--compact .footer__inner {
    padding-bottom: calc(0.65rem + env(safe-area-inset-bottom, 0px));
  }

  .footer__header--desktop,
  .footer__col--shop,
  .footer__col--contact,
  .footer__meta--desktop {
    display: none;
  }

  .footer__grid {
    grid-template-columns: 1fr;
    gap: 0;
  }

  .footer__col--nav-secondary .footer__col-title {
    display: none;
  }

  .footer__col--nav .footer__col-title,
  .footer__col--nav-secondary .footer__col-title {
    margin-bottom: 0.4rem;
    font-size: 0.62rem;
  }

  .footer__col--nav .footer__links,
  .footer__col--nav-secondary .footer__links {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.35rem 0.85rem;
  }

  .footer__col--nav .footer__links li,
  .footer__col--nav-secondary .footer__links li {
    margin-bottom: 0;
  }

  .footer__col--nav .footer__links a,
  .footer__col--nav-secondary .footer__links a {
    font-size: 0.72rem;
    line-height: 1.35;
    letter-spacing: 0.06em;
  }

  .footer__mobile-social {
    display: block;
    margin-top: 0.1rem;
  }

  .footer__mobile-social a {
    padding: 0.28rem 0.55rem;
    font-size: 0.72rem !important;
    letter-spacing: 0.06em;
  }

  .footer__meta--mobile {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.3rem;
    margin-top: 0.4rem;
    text-align: left;
  }

  .footer__brand {
    margin-top: 0.5rem;
  }
}
</style>
